import { useMemo, useState } from 'react'

import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import Divider from '@mui/material/Divider'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { toast } from 'react-toastify'

import type { DiligenceStatus, DiligenceSummary } from '@server/domain/due-diligence'

import CustomChip from '@core/components/mui/chip'
import CustomTextField from '@core/components/mui/text-field'
import { MenuRow } from '@components/menu-row'
import { ModalConfirm } from '@components/modal-confirm'
import { TableDefault } from '@components/table-default'
import DiligenceDialog from '@components/due-diligence/diligence-dialog'

import { useTRPC } from '@/lib/trpc'
import { requireSubscription } from '@/lib/route-guards'
import { DILIGENCE_STATUS, formatDate, percentOf, SELECTABLE_DILIGENCE_STATUS } from '@/lib/due-diligence'

export const Route = createFileRoute('/_dashboard/diligences/')({
  beforeLoad: requireSubscription,
  component: Diligences
})

const columnHelper = createColumnHelper<DiligenceSummary>()

function Diligences() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all' | DiligenceStatus>('all')
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data: diligences = [], isPending } = useQuery(trpc.diligences.list.queryOptions({}))

  const remove = useMutation(
    trpc.diligences.remove.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.diligences.list.queryKey() })
        toast.success('Diligência removida.')
      },
      onError: error => toast.error(error.message)
    })
  )

  const filtered = useMemo(
    () =>
      diligences
        .filter(item => (status === 'all' ? true : item.status === status))
        .filter(item =>
          search ? `${item.projectName} ${item.address} ${item.kind}`.toLowerCase().includes(search.toLowerCase()) : true
        ),
    [diligences, search, status]
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor('projectName', {
        header: 'Projeto',
        cell: ({ row }) => (
          <div className='flex flex-col'>
            <Link
              to='/diligences/$id'
              params={{ id: row.original.id }}
              className='font-medium text-textPrimary no-underline hover:text-primary'
            >
              {row.original.projectName}
            </Link>
            {row.original.address && (
              <Typography variant='caption' color='text.disabled'>
                {row.original.address}
              </Typography>
            )}
          </div>
        )
      }),
      columnHelper.accessor('kind', {
        header: 'Tipo',
        cell: ({ getValue }) => (
          <Typography variant='body2' color='text.secondary'>
            {getValue()}
          </Typography>
        )
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: ({ getValue }) => {
          const config = DILIGENCE_STATUS[getValue()]

          return <CustomChip round='true' size='small' variant='tonal' color={config.color} label={config.label} />
        }
      }),
      columnHelper.accessor('resolvedCount', {
        header: 'Progresso',
        cell: ({ row }) => {
          const percent = percentOf(row.original.resolvedCount, row.original.certificateCount)

          return (
            <div className='flex items-center gap-3 min-is-[160px]'>
              <LinearProgress
                variant='determinate'
                value={percent}
                color={percent === 100 ? 'success' : 'primary'}
                className='is-[88px] bs-[6px] rounded-full'
              />
              <Typography variant='caption' color='text.secondary' className='whitespace-nowrap'>
                {row.original.resolvedCount}/{row.original.certificateCount}
              </Typography>
            </div>
          )
        }
      }),
      columnHelper.accessor('adverseCount', {
        header: 'Ocorrências',
        cell: ({ getValue }) =>
          getValue() > 0 ? (
            <CustomChip round='true' size='small' variant='tonal' color='error' label={String(getValue())} />
          ) : (
            <Typography variant='body2' color='text.disabled'>
              —
            </Typography>
          )
      }),
      columnHelper.accessor('createdAt', {
        header: 'Criada em',
        cell: ({ getValue }) => (
          <Typography variant='body2' color='text.secondary'>
            {formatDate(getValue())}
          </Typography>
        )
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className='flex items-center justify-end'>
            <ModalConfirm
              variant='remove'
              isPending={remove.isPending}
              onConfirm={() => remove.mutate({ id: row.original.id })}
            >
              <Button size='small' className='min-is-0 p-2 text-error'>
                <i className='tabler-trash' />
              </Button>
            </ModalConfirm>
            <MenuRow
              options={[
                {
                  label: 'Abrir diligência',
                  onClick: () => navigate({ to: '/diligences/$id', params: { id: row.original.id } })
                }
              ]}
            />
          </div>
        )
      })
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remove.isPending]
  )

  const table = useReactTable({
    data: filtered,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } }
  })

  return (
    <>
      <Card>
        <CardHeader
          title='Diligências'
          subheader='Todas as operações do workspace'
          action={
            <Button variant='contained' startIcon={<i className='tabler-plus' />} onClick={() => setDialogOpen(true)}>
              Nova diligência
            </Button>
          }
        />
        <Divider />
        <div className='flex flex-wrap items-end justify-between gap-4 p-6'>
          <CustomTextField
            select
            label='Status'
            className='is-[220px]'
            value={status}
            onChange={event => setStatus(event.target.value as typeof status)}
          >
            <MenuItem value='all'>Todos</MenuItem>
            {SELECTABLE_DILIGENCE_STATUS.map(key => (
              <MenuItem key={key} value={key}>
                {DILIGENCE_STATUS[key].label}
              </MenuItem>
            ))}
          </CustomTextField>
          <CustomTextField
            className='is-[280px]'
            placeholder='Buscar projeto, endereço…'
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
        </div>
        <div className='overflow-x-auto'>
          <TableDefault
            table={table}
            withPagination
            onRowClick={row => navigate({ to: '/diligences/$id', params: { id: row.original.id } })}
            emptyMessage={isPending ? 'Carregando…' : 'Nenhuma diligência encontrada com esses filtros.'}
          />
        </div>
      </Card>

      <DiligenceDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  )
}
